import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Eye, Clock, MessageSquare, Pause, CheckCircle,
    XCircle, ArrowLeftRight, Loader2, UserCheck, Ban, Search, UserPlus,
    FileText, Phone, Paperclip, CheckCheck, PhoneIncoming, PhoneMissed, Play, Calendar, User
} from 'lucide-react';

import api from '../services/api';
import { socket } from '../services/socket';
import Modal from '../components/Modal';
import LinkifiedText from '../components/LinkifiedText';

// ==============================================================================
// 1. CONFIGURAÇÕES E UTILITÁRIOS
// ==============================================================================

// 🚨 IP FIXO DA VPS (IMPORTANTE PARA MOBILE)
const BASE_MEDIA_URL = 'https://wadesk.com.br';

const cleanPhone = (p) => p ? p.replace(/\D/g, '').replace('@s.whatsapp.net', '') : '';

// Configuração visual das abas
const STATUS_CONFIG = {
    waiting: { label: 'Fila de Espera', icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-100', border: 'border-yellow-200' },
    open: { label: 'Em Atendimento', icon: MessageSquare, color: 'text-green-600', bg: 'bg-green-100', border: 'border-green-200' },
    paused: { label: 'Pausados', icon: Pause, color: 'text-orange-600', bg: 'bg-orange-100', border: 'border-orange-200' },
    ended: { label: 'Finalizados', icon: CheckCircle, color: 'text-blue-600', bg: 'bg-blue-100', border: 'border-blue-200' },
    canceled: { label: 'Cancelados', icon: Ban, color: 'text-red-600', bg: 'bg-red-100', border: 'border-red-200' },
};

// Componente visual para Chamadas
const CallLogMessage = ({ message }) => {
    const isMissed = message.body?.includes('Perdida') || message.body?.includes('Recusada');
    return (
        <div className="flex justify-center my-4 w-full select-none">
            <div className="bg-gray-100 border border-gray-200 shadow-sm rounded-lg px-4 py-2 flex items-center gap-3 max-w-xs">
                <div className={`p-2 rounded-full ${isMissed ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                    {isMissed ? <PhoneMissed className="w-5 h-5" /> : <PhoneIncoming className="w-5 h-5" />}
                </div>
                <div className="flex flex-col">
                    <span className="text-sm font-bold text-gray-700">
                        {message.body || (isMissed ? 'Chamada de voz perdida' : 'Chamada de voz')}
                    </span>
                    <span className="text-[10px] text-gray-500">
                        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                </div>
            </div>
        </div>
    );
};

// ==============================================================================
// 2. COMPONENTE PRINCIPAL
// ==============================================================================
const AtendimentosScreen = ({ currentUser, companyData }) => {
    // --- ESTADOS DE CATEGORIA ---
    const [categoryModal, setCategoryModal] = useState({ isOpen: false, actionType: null });
    const [categoriesList, setCategoriesList] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const [selectedSubcategoryId, setSelectedSubcategoryId] = useState('');
    const [pauseReason, setPauseReason] = useState('');

    // --- ESTADOS DE DADOS ---
    const [allChats, setAllChats] = useState([]);
    const [usersList, setUsersList] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- ESTADOS DE FILTRO ---
    const [statusFilter, setStatusFilter] = useState('open');
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState(() => {
        const today = new Date();
        const localDateStr = new Date(today.getTime() - (today.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
        return { start: localDateStr, end: localDateStr };
    });

    // --- ESTADOS DE MODAL (Espiar e Ações) ---
    const [selectedChat, setSelectedChat] = useState(null);
    const [chatMessages, setChatMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(false);

    const [isSpyModalOpen, setIsSpyModalOpen] = useState(false);
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
    const [addAttendantModal, setAddAttendantModal] = useState({ isOpen: false });

    const [transferTargetId, setTransferTargetId] = useState('');
    const [selectedAttendants, setSelectedAttendants] = useState([]); // Para adicionar múltiplos
    const [isProcessing, setIsProcessing] = useState(false);

    // --- ESTADOS DE HISTÓRICO ---
    const [historyModal, setHistoryModal] = useState({ isOpen: false, data: [] });
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [historyDetails, setHistoryDetails] = useState({ isOpen: false, ticket: null, messages: [] });

    // --- REF PARA SCROLL AUTOMÁTICO ---
    const messagesEndRef = useRef(null);

    const [clientsList, setClientsList] = useState([]);

    const formatCompanyName = (companyName) => {
        if (!companyName) return '';
        if (companyName.length > 20) {
            return companyName.substring(0, 20) + '...';
        }
        return companyName;
    };

    // ==================================================================================
    // 3. INICIALIZAÇÃO E SOCKET
    // ==================================================================================

    useEffect(() => {
        loadData();
    }, [statusFilter]);

    useEffect(() => {
        // Socket: Atualização em Tempo Real
        socket.on('chat_updated', (updatedChat) => {
            if (updatedChat?.companyId && String(updatedChat.companyId) !== String(currentUser?.companyId)) return;
            if (['ended', 'canceled'].includes(statusFilter)) return;

            setAllChats(prev => {
                const exists = prev.find(c => c.id === updatedChat.id);
                if (exists) {
                    return prev.map(c => c.id === updatedChat.id ? { ...c, ...updatedChat } : c)
                        .sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt));
                }
                return [updatedChat, ...prev];
            });

            // Lógica de "Espiar em Tempo Real"
            if (selectedChat && selectedChat.id === updatedChat.id) {
                setSelectedChat(prev => ({ ...prev, ...updatedChat }));
                if (updatedChat.lastMessage) {
                    setChatMessages(prev => {
                        const exists = prev.some(m => m.timestamp === updatedChat.lastMessage.timestamp);
                        if (!exists) return [...prev, updatedChat.lastMessage];
                        return prev;
                    });
                    scrollToBottom();
                }
            }
        });

        socket.on('new_message', (data) => {
            if (data?.companyId && String(data.companyId) !== String(currentUser?.companyId)) return;
            if (selectedChat && data.phone === selectedChat.contact_id) {
                setChatMessages(prev => [...prev, data.message]);
                scrollToBottom();
            }
        });

        return () => {
            socket.off('chat_updated');
            socket.off('new_message');
        };
    }, [selectedChat, statusFilter]);

    const loadData = async () => {
        setLoading(true);
        try {
            let params = {};
            if (['ended', 'canceled'].includes(statusFilter)) {
                params.status = 'finished';
                params.startDate = dateFilter.start;
                params.endDate = dateFilter.end;
            } else {
                params.status = 'active';
            }

            const [chatsRes, usersRes, clientsRes] = await Promise.all([
                api.get('/chats/monitor', { params }), // Endpoint sem filtro de agente
                api.get('/users'),
                api.get('/clients?limit=1000') // 🆕 Limite até 1000 p/ garantir carregar clientes para o cache local
            ]);

            setAllChats(Array.isArray(chatsRes.data) ? chatsRes.data : (chatsRes.data?.data || []));
            setUsersList(Array.isArray(usersRes.data) ? usersRes.data : (usersRes.data?.data || []));
            const clientsData = clientsRes.data;
            setClientsList(Array.isArray(clientsData) ? clientsData : (clientsData?.clients || []));

            try { const catRes = await api.get('/categories'); setCategoriesList(Array.isArray(catRes.data) ? catRes.data : []); } catch(e) {}
        } catch (e) {
            console.error("Erro ao carregar dados:", e);
        } finally {
            setLoading(false);
        }
    };

    // ==================================================================================
    // 4. LÓGICA DE ESPIAR (BUSCA MENSAGENS)
    // ==================================================================================
    const handleOpenSpy = (chat) => {
        setSelectedChat(chat);
        setChatMessages([]);
        setIsSpyModalOpen(true);
        fetchChatMessages(chat.id);
    };

    const fetchChatMessages = async (chatId) => {
        setLoadingMessages(true);
        try {
            const res = await api.get(`/chats/${chatId}/messages`);
            setChatMessages(Array.isArray(res.data) ? res.data : []);
            scrollToBottom();
        } catch (e) {
            console.error("Erro ao buscar mensagens:", e);
        } finally {
            setLoadingMessages(false);
        }
    };

    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
    };

    // ==================================================================================
    // 5. AÇÕES (TRANSFERIR, STATUS, ADICIONAR ATENDENTE)
    // ==================================================================================

    const handleActionClick = (actionType) => {
        const categorizationRequired = companyData?.settings?.categorizationRequired;
        if (categorizationRequired && (actionType === 'ended' || actionType === 'canceled' || actionType === 'paused')) {
            setSelectedCategoryId('');
            setSelectedSubcategoryId('');
            setPauseReason('');
            setCategoryModal({ isOpen: true, actionType });
            return;
        }
        handleUpdateStatus(actionType);
    };

    const handleUpdateStatus = async (newStatus, extraData = {}) => {
        if (!selectedChat) return;
        const statusNames = { ended: 'Finalizar', canceled: 'Cancelar', paused: 'Pausar', open: 'Retomar' };

        if (!extraData || Object.keys(extraData).length === 0) {
            if (!window.confirm(`Deseja realmente ${statusNames[newStatus]} este atendimento?`)) return;
        }

        setIsProcessing(true);
        try {
            await api.put(`/chats/${selectedChat.id}`, { status: newStatus, ...extraData });

            if (newStatus === 'ended' || newStatus === 'canceled') {
                setIsSpyModalOpen(false);
                setSelectedChat(null);
                if (!['ended', 'canceled'].includes(statusFilter)) {
                    setAllChats(prev => prev.filter(c => c.id !== selectedChat.id));
                }
            } else {
                setSelectedChat(prev => ({ ...prev, status: newStatus }));
                setAllChats(prev => prev.map(c => c.id === selectedChat?.id ? { ...c, status: newStatus } : c));
            }
        } catch (e) {
            alert("Erro ao atualizar status.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleTransfer = async () => {
        if (!transferTargetId || !selectedChat) return;
        setIsProcessing(true);
        try {
            await api.put(`/chats/${selectedChat.id}/transfer`, { agentId: transferTargetId });
            alert("Transferido com sucesso!");
            setIsTransferModalOpen(false);
            setIsSpyModalOpen(false);

            const targetUser = usersList.find(u => u._id === transferTargetId);
            setAllChats(prev => prev.map(c => c.id === selectedChat.id ? { ...c, user_name: targetUser?.name, agentId: targetUser?._id } : c));
            setSelectedChat(null);
        } catch (e) {
            alert("Erro ao transferir.");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleAddAttendant = async () => {
        if (!selectedChat || selectedAttendants.length === 0) return;
        setIsProcessing(true);
        try {
            await api.put(`/chats/${selectedChat.id}/add-attendants`, { attendants: selectedAttendants });
            alert('Atendentes adicionados com sucesso!');
            setAddAttendantModal({ isOpen: false });
            setSelectedAttendants([]);
        } catch (error) {
            alert('Erro ao adicionar atendentes.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleTakeOver = async (chatId) => {
        if (!window.confirm("Deseja assumir este atendimento para você?")) return;
        try {
            await api.put(`/chats/${chatId}`, { status: 'open', agentId: currentUser.id });
            alert("Atendimento assumido! Vá para a aba 'Em Atendimento'.");
            if (statusFilter === 'waiting') {
                setAllChats(prev => prev.filter(c => c.id !== chatId));
            }
        } catch (e) { alert("Erro ao assumir."); }
    };

    // ==================================================================================
    // 5.5 HISTÓRICO DO CLIENTE
    // ==================================================================================
    const handleLoadHistory = async () => {
        if (!selectedChat) return;
        setLoadingHistory(true);
        try {
            const res = await api.get(`/chats/client/${selectedChat.clientId || selectedChat.contact_id}/history`);
            setHistoryModal({ isOpen: true, data: res.data || [] });
        } catch (error) {
            console.error('[HISTORY ERROR]', error);
            alert('Erro ao carregar histórico');
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleOpenHistoryDetails = async (ticket) => {
        setHistoryDetails({ isOpen: true, ticket, messages: [] });
        try {
            const res = await api.get(`/chats/${ticket.id || ticket._id}/messages`);
            setHistoryDetails(prev => ({ ...prev, messages: Array.isArray(res.data) ? res.data : [] }));
        } catch (e) {
            console.error('[HISTORY DETAIL ERROR]', e);
        }
    };

    // ==================================================================================
    // 6. RENDERIZAÇÃO
    // ==================================================================================

    const filteredChats = useMemo(() => {
        let targetStatuses = [];
        if (statusFilter === 'waiting') targetStatuses = ['waiting', 'waiting_evaluation'];
        else if (statusFilter === 'open') targetStatuses = ['open', 'active'];
        else if (statusFilter === 'paused') targetStatuses = ['paused'];
        else if (statusFilter === 'ended') targetStatuses = ['ended', 'finished'];
        else if (statusFilter === 'canceled') targetStatuses = ['canceled'];

        return allChats.filter(chat => {
            const matchesStatus = targetStatuses.includes(chat.status);
            const matchesSearch = (chat.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (chat.contact_id || '').includes(searchTerm);
            return matchesStatus && matchesSearch;
        });
    }, [allChats, statusFilter, searchTerm]);

    const renderMessageBubble = (msg, idx) => {
        const isMe = msg.fromMe;
        const isCall = msg.body?.includes('Chamada') || msg.type === 'call_log';

        return (
            <div key={idx} className={`flex ${isMe ? 'justify-end' : 'justify-start'} mb-2`}>
                <div className={`max-w-[85%] p-2 rounded-lg shadow-sm relative overflow-hidden ${isMe ? 'bg-[#d9fdd3] rounded-tr-none' : 'bg-white rounded-tl-none'}`} style={{overflowWrap: 'anywhere', wordBreak: 'break-all'}}>
                    {isMe && (
                        <p className="text-[10px] font-bold text-blue-800 mb-1 opacity-80 uppercase">
                            {msg.senderName || 'Atendente'}
                        </p>
                    )}
                    {isCall ? (
                        <CallLogMessage message={msg} />
                    ) : (
                        <>
                            {msg.type === 'text' && <LinkifiedText text={msg.body} isUser={isMe} />}
                            {(msg.type === 'image' || msg.type === 'sticker') && (
                                <img src={`${BASE_MEDIA_URL}${msg.mediaUrl}`} className="max-w-xs rounded-lg cursor-pointer hover:opacity-90 mb-1" alt="Mídia" onClick={() => window.open(`${BASE_MEDIA_URL}${msg.mediaUrl}`, '_blank')} />
                            )}
                            {msg.type === 'image' && msg.body && msg.body !== '[Imagem]' && (
                                <p className="text-sm mt-1"><LinkifiedText text={msg.body} /></p>
                            )}
                            {msg.type === 'video' && (
                                <div className="rounded-lg overflow-hidden mb-1 max-w-xs bg-black">
                                    <video controls src={`${BASE_MEDIA_URL}${msg.mediaUrl}`} className="w-full h-auto max-h-[300px]" />
                                </div>
                            )}
                            {msg.type === 'video' && msg.body && msg.body !== '[Vídeo]' && (
                                <p className="text-sm mt-1"><LinkifiedText text={msg.body} /></p>
                            )}
                            {msg.type === 'audio' && (() => {
                                const audioFile = msg.mediaUrl?.split('/').pop();
                                return (
                                    <audio controls className="h-10 min-w-[240px] mt-1 accent-blue-600" preload="metadata">
                                        <source src={`${BASE_MEDIA_URL}/media/audio/${audioFile}`} type="audio/ogg" />
                                        <source src={`${BASE_MEDIA_URL}${msg.mediaUrl}`} type="audio/ogg" />
                                        Seu navegador não suporta reprodução de áudio.
                                    </audio>
                                );
                            })()}
                            {msg.type === 'document' && (
                                <a href={`${BASE_MEDIA_URL}${msg.mediaUrl}`} target="_blank" rel="noreferrer" className="flex items-center text-blue-600 bg-blue-50 p-2 rounded border border-blue-100 hover:bg-blue-100 transition-colors">
                                    <Paperclip className="w-4 h-4 mr-2" />
                                    <span className="underline truncate max-w-[200px]">{msg.fileName || 'Documento'}</span>
                                </a>
                            )}
                            {msg.type === 'document' && msg.body && msg.body !== '[Documento]' && (
                                <p className="text-sm mt-1"><LinkifiedText text={msg.body} /></p>
                            )}
                        </>
                    )}
                    <div className="flex justify-end gap-1 mt-1 items-center">
                        <span className="text-[10px] text-gray-500">
                            {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                        {isMe && <CheckCheck className={`w-3 h-3 ${msg.read ? 'text-blue-500' : 'text-gray-400'}`} />}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="p-6 h-full flex flex-col bg-gray-50 overflow-hidden">

            {/* --- CABEÇALHO --- */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center">
                        <Eye className="w-8 h-8 mr-2 text-blue-600" /> Monitoramento de Atendimentos
                    </h1>
                    <p className="text-gray-500 text-sm">Visualize, assuma ou transfira conversas de toda a equipe.</p>
                </div>

                <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto items-center">
                    {/* Filtro de Data */}
                    <div className="flex items-center gap-2 bg-white px-3 py-2 border rounded-lg shadow-sm">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <input
                            type="date"
                            className="text-gray-600 text-sm outline-none bg-transparent"
                            value={dateFilter.start}
                            onChange={e => setDateFilter(prev => ({ ...prev, start: e.target.value }))}
                        />
                        <span className="text-gray-400">-</span>
                        <input
                            type="date"
                            className="text-gray-600 text-sm outline-none bg-transparent"
                            value={dateFilter.end}
                            onChange={e => setDateFilter(prev => ({ ...prev, end: e.target.value }))}
                        />
                        <button
                            onClick={loadData}
                            className="ml-1 bg-blue-50 text-blue-600 p-1.5 rounded hover:bg-blue-100 transition-colors"
                            title="Filtrar por data"
                        >
                            <Search className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="relative w-full md:w-72">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input type="text" className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-shadow" placeholder="Buscar cliente, telefone..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                    </div>
                </div>
            </div>

            {/* --- ABAS DE STATUS --- */}
            <div className="flex gap-2 overflow-x-auto pb-2 mb-4 border-b border-gray-200 shrink-0">
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <button
                        key={key}
                        onClick={() => setStatusFilter(key)}
                        className={`flex items-center px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all border
                        ${statusFilter === key ? `${config.bg} ${config.color} ${config.border} shadow-sm ring-1 ring-${config.color.split('-')[1]}-200` : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                    >
                        <config.icon className="w-4 h-4 mr-2" /> {config.label}
                    </button>
                ))}
            </div>

            {/* --- LISTAGEM (CARDS) --- */}
            <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
                {loading ? (
                    <div className="flex justify-center items-center h-64"><Loader2 className="w-10 h-10 text-blue-500 animate-spin" /></div>
                ) : filteredChats.length === 0 ? (
                    <div className="text-center text-gray-400 py-20 flex flex-col items-center">
                        <div className="bg-gray-100 p-6 rounded-full mb-4"><Search className="w-10 h-10 opacity-40" /></div>
                        <p className="text-lg font-medium">Nenhum atendimento encontrado.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pb-10">
                        {filteredChats.map(chat => (
                            <div key={chat.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-all duration-200 group flex flex-col justify-between h-56">
                                <div>
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="flex items-center min-w-0">
                                            {chat.profilePicUrl ? (
                                                <img src={chat.profilePicUrl} alt="" className="w-10 h-10 rounded-full mr-3 object-cover border border-gray-200" />
                                            ) : (
                                                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold mr-3 border border-blue-100 shrink-0">
                                                    {chat.name ? chat.name.charAt(0).toUpperCase() : '?'}
                                                </div>
                                            )}
                                            <div className="overflow-hidden">
                                                <h3 className="font-bold text-gray-800 text-sm truncate" title={chat.name}>{chat.name || chat.phone}</h3>
                                                <p className="text-xs text-gray-500 flex items-center truncate">
                                                    <UserCheck className="w-3 h-3 mr-1" />
                                                    {chat.user_name || (chat.agentId && chat.agentId.name) ? <span className="text-blue-600 font-semibold">{chat.user_name || chat.agentId.name}</span> : <span className="text-gray-400 italic">Sem Agente</span>}
                                                </p>
                                                {/* Exibir Empresa se houver e Preferencial se for waiting */}
                                                {(() => {
                                                    const chatClient = clientsList.find(c => cleanPhone(c.phone) === cleanPhone(chat.contact_id));
                                                    return (
                                                        <>
                                                            {chatClient?.company && (
                                                                <p className="text-[10px] text-blue-700 font-bold truncate mt-1">🏢 {formatCompanyName(chatClient.company)}</p>
                                                            )}
                                                            {chat.status === 'waiting' && chatClient?.usePreferredAgent && (
                                                                <p className="text-[10px] text-orange-600 font-bold mt-1 truncate bg-orange-50 px-1 py-0.5 rounded inline-block border border-orange-100">
                                                                    ⭐ Preferencial: {(() => {
                                                                        if (chat.preferredAgentId) {
                                                                            const preffUserObj = usersList.find(u => u._id === chat.preferredAgentId);
                                                                            return preffUserObj ? preffUserObj.name : 'Desconhecido';
                                                                        } else if (chatClient.preferredAgentId) {
                                                                            const preffUserObj = usersList.find(u => u._id === chatClient.preferredAgentId);
                                                                            return preffUserObj ? preffUserObj.name : 'Desconhecido';
                                                                        }
                                                                        return 'Não atribuído';
                                                                    })()}
                                                                </p>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-lg mb-4 h-16 overflow-hidden mt-1">
                                        <p className="text-xs text-gray-600 line-clamp-2 italic">
                                            {(() => {
                                                const msg = chat.lastMessage;
                                                if (!msg) return 'Iniciando atendimento...';
                                                if (typeof msg === 'string') return msg;
                                                if (msg.type === 'audio') return '🎵 Áudio';
                                                if (msg.type === 'image') return '📷 Imagem';
                                                if (msg.type === 'video') return '🎥 Vídeo';
                                                return msg.body || 'Mensagem';
                                            })()}
                                        </p>
                                    </div>
                                    <div className="flex flex-col text-[10px] text-gray-400 mb-3 px-1 mt-auto">
                                        <div className="flex items-center justify-between">
                                            <span>
                                                📅 Abertura: {chat.createdAt ? new Date(chat.createdAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'N/D'}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between mt-0.5">
                                            <span>
                                                ⏱️ Duração: {(() => {
                                                    if (!chat.createdAt) return '--';
                                                    const start = new Date(chat.createdAt);
                                                    const end = chat.status === 'finished' && chat.updatedAt ? new Date(chat.updatedAt) : new Date();
                                                    const diffMs = end - start;
                                                    if (diffMs < 0) return '0 min';
                                                    const diffHrs = Math.floor(diffMs / 3600000);
                                                    const diffMins = Math.floor((diffMs % 3600000) / 60000);
                                                    if (diffHrs > 0) return `${diffHrs}h ${diffMins}m`;
                                                    return `${diffMins} min`;
                                                })()}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    {chat.status === 'waiting' ? (
                                        <button onClick={() => handleTakeOver(chat.id)} className="flex-1 bg-green-600 text-white py-2 rounded-lg text-xs font-bold hover:bg-green-700 flex items-center justify-center transition-colors shadow-sm"><UserPlus className="w-3.5 h-3.5 mr-1.5" /> Assumir</button>
                                    ) : (
                                        <button onClick={() => handleOpenSpy(chat)} className="flex-1 bg-white border border-gray-300 text-gray-700 py-2 rounded-lg text-xs font-bold hover:bg-blue-50 hover:text-blue-600 flex items-center justify-center transition-colors"><Eye className="w-3.5 h-3.5 mr-1.5" /> Monitorar</button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* --- MODAL DE ESPIONAGEM (ATUALIZADO) --- */}
            <Modal
                isOpen={isSpyModalOpen}
                onClose={() => { setIsSpyModalOpen(false); setSelectedChat(null); }}
                // 🚨 ALTERAÇÃO: Max-width maior e flexível
                maxWidth="max-w-6xl w-[95%] md:w-[90%]"
                title=""
            >
                <div className="flex flex-col h-[85vh] md:h-[70vh]">

                    {/* 1. CABEÇALHO DO MODAL COM FOTO E LAYOUT MELHORADO */}
                    <div className="flex flex-col px-4 py-3 border-b border-gray-200 mb-0 shrink-0 bg-gray-50 rounded-t-lg">
                        <div className="flex items-start justify-between w-full">
                            <div className="flex items-center gap-3 overflow-hidden flex-1 min-w-0">
                                {/* FOTO DO PERFIL */}
                                {selectedChat?.profilePicUrl ? (
                                    <img
                                        src={selectedChat.profilePicUrl}
                                        alt=""
                                        className="w-10 h-10 rounded-full object-cover border border-gray-200 shrink-0"
                                    />
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-600 border border-blue-200 shrink-0">
                                        {selectedChat?.name?.charAt(0).toUpperCase()}
                                    </div>
                                )}

                                {/* INFO DO CLIENTE */}
                                <div className="min-w-0 flex flex-col">
                                    <h3 className="text-base font-bold text-gray-800 truncate flex items-center gap-2">
                                        {selectedChat?.name}
                                        <span className="text-[10px] font-normal px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full border border-blue-200 hidden md:inline-block">
                                            Monitorando
                                        </span>
                                    </h3>
                                    {/* EMPRESA */}
                                    {(() => {
                                        const chatClient = clientsList.find(c => cleanPhone(c.phone) === cleanPhone(selectedChat?.contact_id));
                                        if (chatClient?.company) {
                                            return (
                                                <p className="text-[11px] text-blue-700 font-extrabold truncate mt-0.5 tracking-tight flex items-center">
                                                    🏢 {formatCompanyName(chatClient.company)}
                                                </p>
                                            );
                                        }
                                        return null;
                                    })()}
                                    <span className="text-[10px] text-blue-500 font-medium truncate mt-0.5">
                                        {selectedChat?.contact_id ? cleanPhone(selectedChat.contact_id) : ''}
                                    </span>
                                </div>
                            </div>

                            {/* DATA E HORA DE ABERTURA */}
                            {selectedChat?.createdAt && (
                                <div className="hidden md:flex flex-col items-end shrink-0 pl-2 border-l border-gray-200 ml-2">
                                    <span className="text-[10px] text-gray-500 font-bold uppercase">ABERTO EM</span>
                                    <span className="text-xs text-gray-700 font-medium">
                                        {new Date(selectedChat.createdAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* ATENDENTES OCUPANDO A LARGURA TOTAL ABAIXO DA DATA/INFO */}
                        {selectedChat && (selectedChat.user_name || (selectedChat.attendants && selectedChat.attendants.length > 0)) && (
                            <div className="flex items-center gap-1.5 mt-2 flex-wrap w-full">
                                {selectedChat.user_name && (
                                    <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded border border-green-200 font-bold flex items-center gap-1">
                                        <User className="w-3 h-3" /> {selectedChat.user_name}
                                    </span>
                                )}
                                {selectedChat.attendants && selectedChat.attendants.map(attId => {
                                    const attUser = usersList.find(u => u._id === attId);
                                    if (!attUser || attUser.name === selectedChat.user_name) return null;
                                    return (
                                        <span key={attId} className="text-[10px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded border border-gray-200 font-bold flex items-center gap-1">
                                            <User className="w-3 h-3" /> {attUser.name}
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* 2. ÁREA DE MENSAGENS */}
                    <div className="flex-1 bg-[#e5ddd5] border-x border-gray-300 overflow-hidden relative shadow-inner flex flex-col min-h-0">
                        <div className="absolute inset-0 opacity-10 bg-[url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')] bg-repeat pointer-events-none"></div>

                        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar relative z-10 space-y-2">
                            {loadingMessages ? (
                                <div className="flex justify-center items-center h-full flex-col">
                                    <Loader2 className="w-8 h-8 animate-spin text-gray-500 mb-2" />
                                    <p className="text-gray-500 text-xs">Carregando histórico...</p>
                                </div>
                            ) : chatMessages.length === 0 ? (
                                <div className="flex justify-center items-center h-full text-gray-400 flex-col">
                                    <MessageSquare className="w-12 h-12 opacity-20 mb-2" />
                                    <p className="text-sm">Histórico vazio.</p>
                                </div>
                            ) : (
                                chatMessages.map((msg, idx) => renderMessageBubble(msg, idx))
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                    </div>

                    {/* 3. TOOLBAR OTIMIZADA */}
                    {selectedChat && selectedChat.status !== 'ended' && selectedChat.status !== 'canceled' && (
                        <div className="bg-gray-50 p-2 border-t border-x border-b border-gray-200 rounded-b-lg flex flex-nowrap gap-2 justify-between md:justify-end items-center shadow-sm shrink-0 overflow-x-auto no-scrollbar">

                            <button
                                onClick={() => setAddAttendantModal({ isOpen: true })}
                                className="flex items-center justify-center bg-blue-100 text-blue-700 p-2 md:px-3 md:py-1.5 rounded-md text-xs font-bold hover:bg-blue-200 transition-colors whitespace-nowrap"
                                title="Add Atendente"
                            >
                                <UserPlus className="w-4 h-4 md:mr-1.5" /> <span className="hidden md:inline"></span>
                            </button>

                            <button
                                onClick={handleLoadHistory}
                                disabled={loadingHistory}
                                className="flex items-center justify-center bg-purple-50 text-purple-700 p-2 md:px-3 md:py-1.5 rounded-md text-xs font-bold hover:bg-purple-100 transition-colors whitespace-nowrap"
                                title="Histórico de Atendimentos"
                            >
                                {loadingHistory ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                            </button>

                            <div className="h-4 w-px bg-gray-300 mx-1 shrink-0"></div>

                            <button
                                onClick={() => { setIsTransferModalOpen(true); setIsSpyModalOpen(false); }}
                                className="flex items-center justify-center bg-white border border-gray-300 text-gray-700 p-2 md:px-3 md:py-1.5 rounded-md text-xs font-medium hover:bg-gray-50 transition-colors whitespace-nowrap"
                                title="Transferir"
                            >
                                <ArrowLeftRight className="w-4 h-4 md:mr-1.5" /> <span className="hidden md:inline"></span>
                            </button>

                            {selectedChat.status === 'paused' ? (
                                <button onClick={() => handleUpdateStatus('open')} className="flex items-center justify-center bg-green-100 text-green-700 p-2 md:px-3 md:py-1.5 rounded-md text-xs font-bold hover:bg-green-200 transition-colors whitespace-nowrap" title="Retomar">
                                    <CheckCircle className="w-4 h-4 md:mr-1.5" /> <span className="hidden md:inline">Retomar</span>
                                </button>
                            ) : (
                                <button onClick={() => handleActionClick('paused')} className="flex items-center justify-center bg-orange-100 text-orange-700 p-2 md:px-3 md:py-1.5 rounded-md text-xs font-bold hover:bg-orange-200 transition-colors whitespace-nowrap" title="Pausar">
                                    <Pause className="w-4 h-4 md:mr-1.5" /> <span className="hidden md:inline"></span>
                                </button>
                            )}

                            <button
                                onClick={() => handleActionClick('canceled')}
                                className="flex items-center justify-center bg-red-100 text-red-700 p-2 md:px-3 md:py-1.5 rounded-md text-xs font-bold hover:bg-red-200 transition-colors whitespace-nowrap"
                                title="Cancelar"
                            >
                                <Ban className="w-4 h-4 md:mr-1.5" /> <span className="hidden md:inline"></span>
                            </button>

                            <button
                                onClick={() => handleActionClick('ended')}
                                className="flex items-center justify-center bg-green-600 text-white p-2 md:px-3 md:py-1.5 rounded-md text-xs font-bold hover:bg-green-700 shadow-sm transition-colors whitespace-nowrap"
                                title="Finalizar"
                            >
                                <CheckCircle className="w-4 h-4 md:mr-1.5" /> <span className="hidden md:inline"></span>
                            </button>
                        </div>
                    )}

                    {/* Histórico também fica visível em atendimentos já finalizados/cancelados */}
                    {selectedChat && (selectedChat.status === 'ended' || selectedChat.status === 'canceled' || selectedChat.status === 'finished') && (
                        <div className="bg-gray-50 p-2 border-t border-x border-b border-gray-200 rounded-b-lg flex justify-end items-center shadow-sm shrink-0">
                            <button
                                onClick={handleLoadHistory}
                                disabled={loadingHistory}
                                className="flex items-center gap-1.5 bg-purple-50 text-purple-700 px-3 py-1.5 rounded-md text-xs font-bold hover:bg-purple-100 transition-colors"
                            >
                                {loadingHistory ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                                Histórico
                            </button>
                        </div>
                    )}
                </div>
            </Modal>

            {/* --- MODAL ADICIONAR ATENDENTE --- */}
            <Modal
                isOpen={addAttendantModal.isOpen}
                onClose={() => setAddAttendantModal({ isOpen: false })}
                title="Adicionar Atendente"
                confirmText="Adicionar"
                onConfirm={handleAddAttendant}
                isLoading={isProcessing}
            >
                <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar p-1">
                    {(usersList || []).filter(u => u._id !== currentUser.id).map(user => (
                        <label key={user._id} className="flex items-center p-2 border rounded hover:bg-gray-50 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={selectedAttendants.includes(user._id)}
                                onChange={(e) => {
                                    if (e.target.checked) setSelectedAttendants([...selectedAttendants, user._id]);
                                    else setSelectedAttendants(selectedAttendants.filter(id => id !== user._id));
                                }}
                                className="mr-3"
                            />
                            <div className="flex flex-col">
                                <span className="text-sm font-medium text-gray-700">{user.name}</span>
                                <span className="text-xs text-gray-400">{user.email}</span>
                            </div>
                        </label>
                    ))}
                </div>
            </Modal>

            {/* --- MODAL TRANSFERIR --- */}
            <Modal
                isOpen={isTransferModalOpen}
                onClose={() => setIsTransferModalOpen(false)}
                title="Transferir Atendimento"
                confirmText="Confirmar Transferência"
                onConfirm={handleTransfer}
                isLoading={isProcessing}
            >
                <div className="space-y-4">
                    <div className="bg-blue-50 p-3 rounded-lg text-blue-800 text-sm mb-4">
                        Você está transferindo o cliente <strong>{selectedChat?.name}</strong>.
                    </div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Selecione o Agente de Destino:</label>
                    <select
                        className="w-full p-2.5 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 outline-none"
                        value={transferTargetId}
                        onChange={(e) => setTransferTargetId(e.target.value)}
                    >
                        <option value="">Selecione...</option>
                        {usersList.map(u => (
                            <option key={u._id} value={u._id}>
                                {u.name} {u._id === currentUser.id ? '(Você)' : ''}
                            </option>
                        ))}
                    </select>
                </div>
            </Modal>

            {/* --- MODAL HISTÓRICO (LISTA) --- */}
            <Modal
                isOpen={historyModal.isOpen}
                onClose={() => setHistoryModal({ isOpen: false, data: [] })}
                title="Histórico de Atendimentos"
            >
                {historyModal.data.length === 0 ? (
                    <p className="text-center text-gray-400 py-8">Nenhum atendimento anterior encontrado.</p>
                ) : (
                    <div className="space-y-2 max-h-[60vh] overflow-y-auto custom-scrollbar">
                        {historyModal.data.map((ticket) => (
                            <div
                                key={ticket.id || ticket._id}
                                className="p-3 border rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                                onClick={() => handleOpenHistoryDetails(ticket)}
                            >
                                <div className="flex justify-between items-start">
                                    <div>
                                        <p className="text-sm font-bold text-gray-800">{ticket.protocol || `#${(ticket.id || ticket._id)?.slice(-6).toUpperCase()}`}</p>
                                        <p className="text-xs text-gray-500">
                                            Agente: {ticket.user_name || 'N/A'} &bull; Status: <span className="font-medium">{ticket.status}</span>
                                        </p>
                                    </div>
                                    <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                                        {ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString('pt-BR') : ''}
                                    </span>
                                </div>
                                {ticket.lastMessage?.body && (
                                    <p className="text-xs text-gray-500 mt-1 truncate italic">{ticket.lastMessage.body}</p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Modal>

            {/* --- MODAL HISTÓRICO DETALHES --- */}
            <Modal
                isOpen={historyDetails.isOpen}
                onClose={() => setHistoryDetails({ isOpen: false, ticket: null, messages: [] })}
                title={`Visualizando: ${historyDetails.ticket?.protocol || 'Atendimento'}`}
                maxWidth="max-w-2xl"
            >
                <div className="h-[55vh] overflow-y-auto bg-[#e5ddd5] rounded-lg p-3 custom-scrollbar space-y-2">
                    {historyDetails.messages.length === 0 ? (
                        <div className="flex justify-center items-center h-full">
                            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                        </div>
                    ) : (
                        historyDetails.messages.map((msg, idx) => renderMessageBubble(msg, idx))
                    )}
                </div>
            </Modal>

            {/* 🆕 MODAL DE CATEGORIZAÇÃO */}
            {categoryModal.isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                        <div className="p-6 border-b">
                            <h2 className="text-lg font-bold text-gray-800">
                                {categoryModal.actionType === 'ended' ? '🏁 Finalizar' : categoryModal.actionType === 'canceled' ? '🚫 Cancelar' : categoryModal.actionType === 'paused' ? '⏸️ Pausar' : '➡️ Externo'} Atendimento
                            </h2>
                            <p className="text-sm text-gray-500 mt-1">Selecione a categoria para registrar este atendimento.</p>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <label className="text-sm font-bold text-gray-700 block mb-1">Categoria *</label>
                                <select
                                    value={selectedCategoryId}
                                    onChange={e => { setSelectedCategoryId(e.target.value); setSelectedSubcategoryId(''); }}
                                    className="w-full border rounded-lg p-2.5 bg-white text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                >
                                    <option value="">Selecione uma categoria...</option>
                                    {categoriesList.map(c => (
                                        <option key={c._id} value={c._id}>{c.name}</option>
                                    ))}
                                </select>
                            </div>

                            {selectedCategoryId && (() => {
                                const cat = categoriesList.find(c => c._id === selectedCategoryId);
                                return cat && cat.subcategories && cat.subcategories.length > 0 ? (
                                    <div>
                                        <label className="text-sm font-bold text-gray-700 block mb-1">Subcategoria *</label>
                                        <select
                                            value={selectedSubcategoryId}
                                            onChange={e => setSelectedSubcategoryId(e.target.value)}
                                            className="w-full border rounded-lg p-2.5 bg-white text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                        >
                                            <option value="">Selecione uma subcategoria...</option>
                                            {cat.subcategories.map((sub, idx) => (
                                                <option key={sub._id || idx} value={idx}>{sub.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                ) : null;
                            })()}
                            
                            {/* ⏸️ PAUSE REASON NA CATEGORIZAÇÃO */}
                            {categoryModal.actionType === 'paused' && (
                                <div className="mt-4">
                                    <label className="text-sm font-bold text-gray-700 block mb-1">Motivo da Pausa *</label>
                                    <textarea
                                        value={pauseReason}
                                        onChange={(e) => setPauseReason(e.target.value)}
                                        className="w-full p-3 border rounded-lg text-sm bg-gray-50 focus:bg-white transition-colors min-h-[80px]"
                                        placeholder="Ex: Aguardando envio de documento do cliente..."
                                        required
                                    />
                                </div>
                            )}
                        </div>
                        <div className="p-4 border-t flex gap-3 justify-end">
                            <button
                                onClick={() => setCategoryModal({ isOpen: false, actionType: null })}
                                className="px-4 py-2 rounded-lg border text-gray-600 hover:bg-gray-50 text-sm font-medium"
                            >
                                Cancelar
                            </button>
                            <button
                                disabled={!selectedCategoryId || isProcessing}
                                onClick={async () => {
                                    const cat = categoriesList.find(c => c._id === selectedCategoryId);
                                    const sub = selectedSubcategoryId !== '' ? cat?.subcategories?.[Number(selectedSubcategoryId)] : null;
                                    const action = categoryModal.actionType;
                                    
                                    if (action === 'paused' && !pauseReason.trim()) {
                                        alert('Por favor, informe o motivo da pausa.');
                                        return;
                                    }

                                    setCategoryModal({ isOpen: false, actionType: null });
                                    await handleUpdateStatus(action, {
                                        categoryId: selectedCategoryId,
                                        categoryName: cat?.name,
                                        subcategoryId: sub ? sub._id : undefined,
                                        subcategoryName: sub ? sub.name : undefined,
                                        ...(action === 'paused' ? { pauseReason } : {})
                                    });
                                }}
                                className={`px-4 py-2 rounded-lg text-sm font-bold text-white transition-colors ${categoryModal.actionType === 'ended'
                                    ? 'bg-green-600 hover:bg-green-700 disabled:bg-green-300'
                                    : categoryModal.actionType === 'canceled'
                                    ? 'bg-red-600 hover:bg-red-700 disabled:bg-red-300'
                                    : 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300'
                                    } disabled:cursor-not-allowed`}
                            >
                                {isProcessing ? 'Aguarde...' : categoryModal.actionType === 'ended' ? 'Confirmar Finalização' : categoryModal.actionType === 'canceled' ? 'Confirmar Cancelamento' : categoryModal.actionType === 'paused' ? 'Confirmar Pausa' : 'Confirmar Transferência'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default AtendimentosScreen;